// ProjectDetailsPage.js - Complete working component

import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, Loader, AlertCircle } from 'lucide-react';
import '../css/ProjectDetailsPage.css';

const ProjectDetailsPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [project, setProject] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [comments, setComments] = useState([]);
  const [newComment, setNewComment] = useState('');

  useEffect(() => {
    // Check authentication
    const token = localStorage.getItem('authToken');
    if (!token) {
      navigate('/login');
      return;
    }

    fetchProject();
  }, [id, navigate]);

  // Fetch project from API
  const fetchProject = async () => {
    setLoading(true);
    setError(null);
    
    try {
      const token = localStorage.getItem('authToken');
      const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000';
      
      const response = await fetch(`${API_BASE_URL}/api/projects/${id}/`, {
        headers: {
          'Authorization': `Token ${token}`,
          'Content-Type': 'application/json',
        },
      });

      if (response.status === 404) {
        setError('Project not found');
        setProject(null);
        setLoading(false);
        return;
      }

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || `HTTP error! status: ${response.status}`);
      }

      const projectData = await response.json();
      setProject(projectData);
      
      // Load existing comments (mock for now)
      setComments([
        {
          id: 1,
          text: "Initial project requirements have been reviewed and approved.",
          author: "Project Manager",
          timestamp: "2 days ago"
        },
        {
          id: 2,
          text: "Great work on the initial concepts! Looking forward to seeing the final designs.",
          author: "Client",
          timestamp: "1 day ago"
        }
      ]);

    } catch (error) {
      console.error('Failed to fetch project:', error);
      setError(error.message || 'Failed to load project');
      setProject(null);
    } finally {
      setLoading(false);
    }
  };

  // Handle case where project is loading
  if (loading) {
    return (
      <div className="project-details-container">
        <div className="loading-message" style={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          minHeight: '60vh',
          gap: '20px'
        }}>
          <Loader className="animate-spin" size={48} />
          <h2>Loading...</h2>
          <p>Please wait while we fetch the project details.</p>
        </div>
      </div>
    );
  }

  // Handle case where there's an error or project is not found
  if (error || !project) {
    return (
      <div className="project-details-container">
        <div className="error-message" style={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          minHeight: '60vh',
          gap: '20px',
          textAlign: 'center'
        }}>
          <AlertCircle size={48} color="#e74c3c" />
          <h2>Project Not Found</h2>
          <p>
            {error || `The project with ID ${id} could not be found. Please check the URL or go back to the dashboard.`}
          </p>
          <button 
            onClick={() => navigate('/dashboard2')} 
            className="btn btn-primary"
            style={{
              padding: '12px 24px',
              backgroundColor: '#007bff',
              color: 'white',
              border: 'none',
              borderRadius: '8px',
              cursor: 'pointer',
              fontSize: '16px'
            }}
          >
            Back to Dashboard
          </button>
        </div>
      </div>
    );
  }

  const statusColor = (status) => {
    switch (status?.toLowerCase()) {
      case 'draft': return 'status-draft';
      case 'in_review': 
      case 'in review': return 'status-in-review';
      case 'in_progress':
      case 'in progress': return 'status-in-progress';
      case 'revision_requested':
      case 'revision requested': return 'status-revision-requested';
      case 'published': 
      case 'completed': return 'status-published';
      case 'cancelled': return 'status-cancelled';
      default: return 'status-default';
    }
  };

  const getStepFromStatus = (status) => {
    switch (status?.toLowerCase()) {
      case 'draft': return 1;
      case 'in_review': return 2;
      case 'in_progress': return 3;
      case 'published':
      case 'completed': return 5;
      default: return project.step || 1;
    }
  };

  const handleCommentSubmit = (e) => {
    e.preventDefault();
    if (newComment.trim()) {
      setComments([...comments, {
        id: Date.now(),
        text: newComment,
        author: 'You',
        timestamp: new Date().toLocaleString()
      }]);
      setNewComment('');
    }
  };

  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  const getFileIcon = (fileName) => {
    const extension = fileName?.split('.').pop()?.toLowerCase();
    switch (extension) {
      case 'pdf': return '📄';
      case 'doc':
      case 'docx': return '📝';
      case 'xls':
      case 'xlsx': return '📊';
      case 'ppt':
      case 'pptx': return '📋';
      case 'jpg':
      case 'jpeg':
      case 'png':
      case 'gif': return '🖼️';
      case 'zip':
      case 'rar': return '📦';
      case 'figma': return '🎨';
      case 'ai': return '🖌️';
      default: return '📎';
    }
  };

  const handleBackClick = () => {
    navigate('/dashboard2');
  };

  const formatBudget = (budget) => {
    const budgetMap = {
      'under_1k': 'Below ₹1,000',
      '1k_5k': '₹1,000 - ₹5,000',
      '5k_10k': '₹5,000 - ₹10,000',
      '10k_50k': '₹10,000 - ₹50,000',
      'above_50k': 'Above ₹50,000'
    };
    return budgetMap[budget] || budget;
  };

  const formatTimeline = (timeline) => {
    const timelineMap = {
      '1_week': '1 Week',
      '2_weeks': '2 Weeks',
      '1_month': '1 Month',
      'flexible': 'Flexible'
    };
    return timelineMap[timeline] || timeline;
  };

  const formatCategory = (category) => {
    const categoryMap = {
      'strategy': 'Strategy',
      'graphic': 'Graphic Design',
      'website': 'Website Creation',
      'video': 'Motion & Video',
      'social_media': 'Social Media',
      'app': 'App Creation',
      'sound': 'Sound Design',
      'ai_ar_vr': 'AI, AR & VR'
    };
    return categoryMap[category] || category;
  };

  const currentStep = getStepFromStatus(project.status);

  return (
    <div className="project-details-container">
      {/* Back Button */}
      <button className="back-button" onClick={handleBackClick}>
        <ArrowLeft size={20} />
        Back to Dashboard
      </button>

      {/* Header */}
      <div className="header">
        <h2 className="title">{project.title || 'Untitled Project'}</h2>
        <span className={`status-badge ${statusColor(project.status)}`}>
          {(project.status || 'DRAFT').toUpperCase().replace('_', ' ')}
        </span>
      </div>
      <p className="submitted-date">
        Submitted {new Date(project.created_at).toLocaleDateString()}
      </p>
      
      {/* Custom Stepper */}
      <div className="stepper-container">
        <div className="stepper">
          {['Submitted', 'Accepted', 'In Progress', 'Review', 'Published'].map((step, index) => (
            <div key={index} className="step-item">
              <div className={`step-circle ${index < currentStep ? 'active' : ''}`}>
                {index < currentStep ? '✓' : index + 1}
              </div>
              <span className={`step-label ${index < currentStep ? 'active' : ''}`}>
                {step}
              </span>
              {index < 4 && <div className={`step-line ${index < currentStep - 1 ? 'active' : ''}`}></div>}
            </div>
          ))}
        </div>
      </div>
      
      {/* Info */}
      <div className="info-grid">
        <div className="info-section">
          <h4 className="info-title">Description</h4>
          <p className="info-text">{project.description || 'No description provided'}</p>
        </div>
        <div className="info-section">
          <h4 className="info-title">Details</h4>
          <ul className="details-list">
            <li>Category: {formatCategory(project.category) || 'Not specified'}</li>
            <li>Budget: {formatBudget(project.budget) || 'Not specified'}</li>
            <li>Timeline: {formatTimeline(project.timeline) || 'Not specified'}</li>
            <li>Tokens Used: {project.use_tokens ? `Yes (${project.tokens_used || 0} tokens)` : 'No'}</li>
          </ul>
        </div>
      </div>
      
      {/* Files */}
      {project.files && project.files.length > 0 && (
        <div className="files-section">
          <h4 className="section-title">Attached Files</h4>
          <div className="files-container">
            {project.files.map((file, idx) => (
              <div key={idx} className="file-card">
                <div className="file-icon">{getFileIcon(file.name)}</div>
                <div className="file-info">
                  <span className="file-name">{file.name || 'Unknown file'}</span>
                  <span className="file-size">{formatFileSize(file.size || 0)}</span>
                </div>
                <button 
                  className="file-download" 
                  onClick={() => file.file && window.open(file.file, '_blank')}
                >
                  ⬇️
                </button>
              </div>
            ))}
          </div>
        </div>
      )}
      
      {/* Chat */}
      <div className="chat-section">
        <h4 className="section-title">Conversation</h4>
        <div className="comment-box">
          <div className="comments-list">
            {comments.length === 0 ? (
              <p className="no-comments">No comments yet. Start the conversation!</p>
            ) : (
              comments.map((comment) => (
                <div key={comment.id} className="comment">
                  <div className="comment-header">
                    <span className="comment-author">{comment.author}</span>
                    <span className="comment-timestamp">{comment.timestamp}</span>
                  </div>
                  <p className="comment-text">{comment.text}</p>
                </div>
              ))
            )}
          </div>
          <form onSubmit={handleCommentSubmit} className="comment-form">
            <div className="comment-input-container">
              <textarea
                value={newComment}
                onChange={(e) => setNewComment(e.target.value)}
                placeholder="Add a comment..."
                className="comment-input"
                rows="3"
              ></textarea>
              <button type="submit" className="comment-submit">
                <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M14.536 21.686a.5.5 0 0 0 .937-.024l6.5-19a.496.496 0 0 0-.635-.635l-19 6.5a.5.5 0 0 0-.024.937l7.93 3.18a2 2 0 0 1 1.112 1.11z"/>
                  <path d="m21.854 2.147-10.94 10.939"/>
                </svg>
              </button>
            </div>
          </form>
        </div>
      </div>
      
      {/* Actions */}
      <div className="actions-section">
        {project.status === 'in_review' && (
          <button className="btn btn-approve">Approve & Publish</button>
        )}
        {project.status !== 'published' && project.status !== 'completed' && (
          <button className="btn btn-revision">Request Revision</button>
        )}
        {project.status === 'draft' && (
          <button className="btn btn-cancel">Cancel Project</button>
        )}
      </div>
    </div>
  );
};

export default ProjectDetailsPage;